import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/auth/AuthContext";
import { useLabAccess } from "../../contexts/LabAccessContext";
import "./Pricing.css";

const LAB_IMAGES = {
    1: "/static/mdb/img/astrobee.jpg",
    2: "/static/mdb/img/spheres.jpg",
    3: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRLatXIK9n0QsA3zf02dF3CVWi_Kqj0FyONoBiS-WK-cA&s=10",
};

const LAB_ORGANIZATIONS = {
    1: "NASA Ames Research Center",
    2: "MIT Space Systems Laboratory",
    3: "JAXA",
};

export default function Pricing() {
    const { labId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const { user, isAuthenticated, loading: authLoading } = useAuth();
    const {
        labs: accessLabs,
        loading: labAccessLoading,
    } = useLabAccess();

    const lab = Array.isArray(accessLabs)
        ? accessLabs.find(x => String(x.id) === String(labId))
        : null;

    const plans = Array.isArray(lab?.plans) ? lab.plans : [];

    const [subscriptionStatus, setSubscriptionStatus] = useState("free");
    const [subscriptionPlan, setSubscriptionPlan] = useState(null);
    const [subscriptionLoading, setSubscriptionLoading] = useState(true);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        if (labAccessLoading) {
            setSubscriptionLoading(true);
            return;
        }

        if (!lab) {
            setSubscriptionStatus("free");
            setSubscriptionPlan(null);
        } else if (lab.subscribed && lab.subscription) {
            setSubscriptionStatus(lab.subscription.status || "active");
            setSubscriptionPlan(lab.subscription.plan || null);
        } else {
            setSubscriptionStatus("free");
            setSubscriptionPlan(null);
        }

        setSubscriptionLoading(false);
    }, [lab, labAccessLoading]);

    const userHasLabAccess = Boolean(lab?.subscribed);
    const hasActiveSubscription = ["active", "trialing"].includes(subscriptionStatus);
    const labName = lab?.name || "Space Robotics Laboratory";
    const organization = LAB_ORGANIZATIONS[String(labId)] || "STEMX365";
    const labImage =
        LAB_IMAGES[String(labId)] ||
        lab?.image_url ||
        "/static/mdb/img/lab-default.jpg";

    const isInstitutionPlan = plan => {
        const id = String(plan?.id || "").toLowerCase();
        return id === "institution" || id.endsWith("_institution");
    };

    const handlePlan = async plan => {
        if (checkoutLoading || !plan?.id) {
            if (plan && !plan.id) setErrorMessage("Invalid pricing plan.");
            return;
        }

        const planId = plan.id;

        if (isInstitutionPlan(plan)) {
            navigate(
                `/rect/contact?plan=${encodeURIComponent(planId)}&labId=${encodeURIComponent(labId)}`
            );
            return;
        }

        if (authLoading) return;

        if (!isAuthenticated || !user) {
            const checkoutPath =
                `/rect/app/labs/billing/checkout?labId=${encodeURIComponent(labId)}` +
                `&plan=${encodeURIComponent(planId)}`;

            navigate(
                `/rect/app/labs/signup?labId=${encodeURIComponent(labId)}` +
                `&plan=${encodeURIComponent(planId)}` +
                `&redirect=${encodeURIComponent(checkoutPath)}`,
                {
                    state: {
                        from: location.pathname,
                        labId: String(labId),
                        plan: planId,
                    },
                }
            );
            return;
        }

        if (userHasLabAccess) {
            navigate(`/rect/app/labs/${encodeURIComponent(labId)}`);
            return;
        }

        navigate(
            `/rect/app/labs/billing/checkout?labId=${encodeURIComponent(labId)}` +
            `&plan=${encodeURIComponent(planId)}`
        );
    };

    const handleOpenLab = () =>
        navigate(`/rect/app/labs/${encodeURIComponent(labId)}`);

    if (!labAccessLoading && !lab) {
        return (
            <div className="pricing-page">
                <section className="pricing-hero">
                    <div className="pricing-eyebrow">STEMX365 LABS</div>
                    <h1>Lab Not Found</h1>
                    <p>The requested robotics laboratory does not exist.</p>
                    <button className="plan-button" onClick={() => navigate("/labs")}>
                        Browse Labs
                    </button>
                </section>
            </div>
        );
    }

    if (labAccessLoading || !lab) {
        return (
            <div className="pricing-page">
                <section className="pricing-hero">
                    <div className="pricing-eyebrow">
                        STEMX365 SPACE ROBOTICS
                    </div>
                    <h1>Loading...</h1>
                    <p>
                        Loading {labName} pricing and laboratory information.
                    </p>
                </section>
            </div>
        );
    }

    return (
        <div className="pricing-page">
            <section className="pricing-hero">
                <div className="pricing-eyebrow">STEMX365 SPACE ROBOTICS</div>

                <h1>
                    Start building with <span>{labName}.</span>
                </h1>

                <p>
                    Get access to the STEMX365 robotics environment and start
                    programming {labName} directly from your browser.
                </p>

                <div className="pricing-selected-lab">
                    <img src={labImage} alt={labName} />
                    <div>
                        <strong>{labName}</strong>
                        <span>{organization}</span>
                    </div>
                </div>

                <div className="billing-note">
                    <span className="billing-dot" />
                    Cancel anytime. No long-term contract.
                </div>
            </section>

            {isAuthenticated && userHasLabAccess && (
                <section className="pricing-access-banner">
                    <div>
                        <strong>You already have access to {labName}.</strong>
                        <p>Your laboratory is ready to launch.</p>
                    </div>

                    <button className="plan-button" onClick={handleOpenLab}>
                        Open Lab →
                    </button>
                </section>
            )}

            {errorMessage && (
                <div className="pricing-error">{errorMessage}</div>
            )}

            <section className="pricing-section">
                <div className="pricing-section-header">
                    <div>
                        <span>{labName.toUpperCase()}</span>
                        <h2>Choose your plan</h2>
                        <p>Select the plan that fits your {labName} mission.</p>
                    </div>
                </div>

                {plans.length === 0 ? (
                    <div className="pricing-empty">
                        <h3>Plans unavailable</h3>
                        <p>
                            There are currently no plans available for this
                            laboratory.
                        </p>
                    </div>
                ) : (
                    <div className="pricing-grid">
                        {plans.map(plan => (
                            <PricingCard
                                key={plan.id}
                                plan={plan}
                                onSelect={() => handlePlan(plan)}
                                isAuthenticated={isAuthenticated}
                                authLoading={authLoading}
                                hasLabAccess={userHasLabAccess}
                                subscriptionLoading={subscriptionLoading}
                                hasActiveSubscription={hasActiveSubscription}
                                checkoutLoading={checkoutLoading}
                                isInstitutionPlan={isInstitutionPlan(plan)}
                            />
                        ))}
                    </div>
                )}
            </section>

            <section className="platform-section">
                <div className="pricing-section-header">
                    <h2>Everything you need to build a space robotics mission.</h2>
                </div>

                <div className="platform-grid">
                    <Feature
                        icon="💻"
                        title="Browser IDE"
                        text="Write and test robot software without installing a local robotics environment."
                    />
                    <Feature
                        icon="🛰️"
                        title="Robot Simulation"
                        text={`Run ${labName} simulations using your own code.`}
                    />
                    <Feature
                        icon="🎛️"
                        title="Mission Control"
                        text="Command your robot and monitor its state from a browser-based control center."
                    />
                    <Feature
                        icon="📡"
                        title="Live Telemetry"
                        text="Monitor position, attitude, velocity and other robot telemetry in real time."
                    />
                    <Feature
                        icon="🧪"
                        title="Experiments"
                        text="Save experiments, compare results, and build a history of your missions."
                    />
                    <Feature
                        icon="☁️"
                        title="Cloud Infrastructure"
                        text="STEMX365 manages the simulation infrastructure so you can focus on robotics."
                    />
                </div>
            </section>

            <section className="institution-section">
                <div>
                    <div className="pricing-eyebrow">FOR UNIVERSITIES & RESEARCH</div>
                    <h2>
                        Bring {labName}
                        <br />
                        to your organization.
                    </h2>
                    <p>
                        Give your students and researchers access to {labName}
                        without having to build and maintain the infrastructure
                        yourself.
                    </p>
                </div>

                <button
                    onClick={() =>
                        navigate(
                            `/rect/contact?plan=${encodeURIComponent(
                                `${lab.slug}_institution`
                            )}&labId=${encodeURIComponent(labId)}`
                        )
                    }
                >
                    Talk to Us
                </button>
            </section>

            <section className="pricing-faq">
                <div className="pricing-section-header">
                    <h2>Frequently asked questions</h2>
                </div>

                <div className="faq-grid">
                    <FAQ
                        question={`Do I need to install software to use ${labName}?`}
                        answer="No. STEMX365 provides the robotics development environment and simulation infrastructure. You develop from your browser."
                    />
                    <FAQ
                        question="What happens after I subscribe?"
                        answer="After your payment is confirmed, your laboratory access is activated and you can launch the environment from your dashboard."
                    />
                    <FAQ
                        question="Can I cancel my subscription?"
                        answer="Yes. You can cancel your subscription at any time. Your access remains available through the current billing period."
                    />
                    <FAQ
                        question="Can institutions purchase access?"
                        answer={`Yes. Universities, schools, and research organizations can contact STEMX365 for ${labName} institutional access.`}
                    />
                </div>
            </section>
        </div>
    );
}

function PricingCard({
    plan,
    onSelect,
    isAuthenticated,
    authLoading,
    hasLabAccess,
    subscriptionLoading,
    hasActiveSubscription,
    checkoutLoading,
    isInstitutionPlan,
}) {
    const price = plan?.price != null ? Number(plan.price) : null;
    const features = Array.isArray(plan?.features) ? plan.features : [];

    let buttonText = "Start Building";
    let disabled = false;

    if (isInstitutionPlan) {
        buttonText = "Contact Us";
    } else if (authLoading || subscriptionLoading && isAuthenticated && !hasLabAccess) {
        buttonText = "Checking...";
        disabled = true;
    } else if (!isAuthenticated) {
        buttonText = "Get Started";
    } else if (hasLabAccess) {
        buttonText = "Open Lab →";
    } else if (hasActiveSubscription) {
        buttonText = "Choose Plan";
    } else {
        buttonText = "Start Mission";
    }

    if (checkoutLoading) {
        buttonText = "Please wait...";
        disabled = true;
    }

    return (
        <div
            className={`pricing-card ${plan?.popular ? "popular" : ""} ${
                hasLabAccess && !isInstitutionPlan ? "current-plan" : ""
            }`}
        >
            {plan?.popular && (
                <div className="popular-badge">MOST POPULAR</div>
            )}

            {plan?.current && (
                <div className="current-plan-badge">CURRENT PLAN</div>
            )}

            {hasLabAccess && !isInstitutionPlan && !plan?.current && (
                <div className="current-plan-badge">LAB ACCESS ACTIVE</div>
            )}

            <div className="plan-header">
                <h3>{plan?.name}</h3>
                <p>{plan?.description}</p>
            </div>

            <div className="plan-price">
                {price !== null ? (
                    <>
                        <span className="currency">$</span>
                        <span className="amount">{price}</span>
                        <span className="period">/ month</span>
                    </>
                ) : (
                    <span className="custom-price">Custom</span>
                )}
            </div>

            <button
                className="plan-button"
                onClick={onSelect}
                disabled={disabled}
            >
                {buttonText}
            </button>

            <div className="features-title">Includes:</div>

            <ul className="plan-features">
                {features.map((feature, index) => (
                    <li key={`${plan.id}-${index}`}>
                        <span className="check">✓</span>
                        {feature}
                    </li>
                ))}
            </ul>
        </div>
    );
}

function Feature({ icon, title, text }) {
    return (
        <div className="platform-feature">
            <div className="platform-feature-icon">{icon}</div>
            <h3>{title}</h3>
            <p>{text}</p>
        </div>
    );
}

function FAQ({ question, answer }) {
    return (
        <div className="faq-item">
            <h3>{question}</h3>
            <p>{answer}</p>
        </div>
    );
}